import { useState } from "react";

type Category = "all" | "solar" | "gas" | "whole-home";
type PriceBucket = "all" | "under500" | "500to1000" | "1000to2500" | "2500to5000" | "over5000";

interface Props {
  activeCategory: Category;
  onCategoryChange: (c: Category) => void;
  showInStock: boolean;
  showOutOfStock: boolean;
  onAvailabilityChange: (inStock: boolean, outOfStock: boolean) => void;
  priceBucket: PriceBucket;
  onPriceBucketChange: (b: PriceBucket) => void;
  selectedBrands: string[];
  onBrandsChange: (brands: string[]) => void;
  inStockCount: number;
  outOfStockCount: number;
}

const categories: { id: Category; label: string }[] = [
  { id: "all", label: "All Products" },
  { id: "solar", label: "Solar & Battery" },
  { id: "gas", label: "Gas Generators" },
  { id: "whole-home", label: "Whole Home" },
];

const priceBuckets: { id: PriceBucket; label: string }[] = [
  { id: "all", label: "All Prices" },
  { id: "under500", label: "Under $500" },
  { id: "500to1000", label: "$500 \u2013 $1,000" },
  { id: "1000to2500", label: "$1,000 \u2013 $2,500" },
  { id: "2500to5000", label: "$2,500 \u2013 $5,000" },
  { id: "over5000", label: "Over $5,000" },
];

const allBrands = [
  "EcoFlow",
  "Nature's Generator",
  "Renogy",
];

function FilterSection({ title, children, defaultOpen = true }: {
  title: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 pb-4 mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-xs font-bold text-gray-700 uppercase tracking-widest mb-3 cursor-pointer"
      >
        {title}
        <div className="w-4 h-4 flex items-center justify-center text-gray-400">
          <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
        </div>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

export default function ShopSidebar({
  activeCategory, onCategoryChange,
  showInStock, showOutOfStock, onAvailabilityChange,
  priceBucket, onPriceBucketChange,
  selectedBrands, onBrandsChange,
  inStockCount, outOfStockCount,
}: Props) {

  const toggleBrand = (brand: string) => {
    if (selectedBrands.includes(brand)) {
      onBrandsChange(selectedBrands.filter((b) => b !== brand));
    } else {
      onBrandsChange([...selectedBrands, brand]);
    }
  };

  const hasActiveFilters =
    activeCategory !== "all" ||
    !showInStock || !showOutOfStock ||
    priceBucket !== "all" ||
    selectedBrands.length > 0;

  const clearAll = () => {
    onCategoryChange("all");
    onAvailabilityChange(true, true);
    onPriceBucketChange("all");
    onBrandsChange([]);
  };

  return (
    <aside className="w-full lg:w-56 shrink-0">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-widest flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center text-amber-500">
            <i className="ri-equalizer-line"></i>
          </div>
          Filters
        </h3>
        {hasActiveFilters && (
          <button
            onClick={clearAll}
            className="text-xs text-amber-600 hover:text-amber-700 font-medium cursor-pointer whitespace-nowrap"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Availability */}
      <FilterSection title="Availability">
        <label className="flex items-center gap-2 text-sm text-gray-600 mb-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInStock}
            onChange={(e) => onAvailabilityChange(e.target.checked, showOutOfStock)}
            className="accent-amber-500 w-4 h-4 cursor-pointer rounded"
          />
          <span>In stock</span>
          <span className="ml-auto text-xs text-gray-400">({inStockCount})</span>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showOutOfStock}
            onChange={(e) => onAvailabilityChange(showInStock, e.target.checked)}
            className="accent-amber-500 w-4 h-4 cursor-pointer rounded"
          />
          <span>Out of stock</span>
          <span className="ml-auto text-xs text-gray-400">({outOfStockCount})</span>
        </label>
      </FilterSection>

      {/* Price */}
      <FilterSection title="Price">
        <div className="space-y-2">
          {priceBuckets.map((b) => (
            <label key={b.id} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="radio"
                name="price"
                checked={priceBucket === b.id}
                onChange={() => onPriceBucketChange(b.id)}
                className="accent-amber-500 w-4 h-4 cursor-pointer"
              />
              {b.label}
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Category */}
      <FilterSection title="Product Type">
        <div className="space-y-2">
          {categories.map((cat) => (
            <label key={cat.id} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="radio"
                name="category"
                checked={activeCategory === cat.id}
                onChange={() => onCategoryChange(cat.id)}
                className="accent-amber-500 w-4 h-4 cursor-pointer"
              />
              {cat.label}
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Brand */}
      <FilterSection title="Brand" defaultOpen={true}>
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {allBrands.map((brand) => (
            <label key={brand} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectedBrands.includes(brand)}
                onChange={() => toggleBrand(brand)}
                className="accent-amber-500 w-4 h-4 cursor-pointer rounded"
              />
              {brand}
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Expert Help CTA */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
        <p className="text-xs font-bold text-gray-800 mb-1">Need help choosing?</p>
        <p className="text-xs text-gray-500 mb-3">Our power experts match you with the right system.</p>
        <a
          href="tel:8666054975"
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold px-3 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
        >
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-phone-line"></i>
          </div>
          (866) 605-4975
        </a>
      </div>
    </aside>
  );
}
